import { MesfModal, TimeService, useShiftNavigator } from "@dexteel/mesf-core";
import { Checkbox, FormControl, FormControlLabel, Grid } from "@mui/material";
import { DateTimePicker } from "@mui/x-date-pickers";
import { isNil } from "lodash-es";
import moment from "moment";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { LazyLoading } from "../../../../controls/LazyLoading";
import { FooterModalButtons } from "../../../../controls/buttons/FooterModalButtons";
import { useEAFProcessesContext } from "../context/EAFProcessesContext";
import { ProcessByShift } from "../models/ProcessByShift";
import { upsertProcess } from "../repositories/EAFRepository";

type Props = {
  show: boolean;
  onHide: (shouldUpdate: boolean) => void;
  refreshData: () => Promise<void>;
};
export const UpsertEAFProcess = ({ show, onHide, refreshData }: Props) => {
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isNew, setIsNew] = useState(false);
  const [isNotEnd, setIsNotEnd] = useState(false);
  const [title, setTitle] = useState("");
  const { shiftInfo, getShiftDataFromAPI } = useShiftNavigator();

  const {
    state: { selectedProcess },
    actions: {
      setNotificationMessage,
      setErrorMessage,
      setSelectedProcess,
      setProcessSelected,
    },
  } = useEAFProcessesContext();

  const { register, control, handleSubmit, reset, formState } =
    useForm<ProcessByShift>({
      defaultValues: {},
      mode: "onChange",
    });

  const onSubmit: SubmitHandler<ProcessByShift> = async (
    data: ProcessByShift,
  ) => {
    setIsSubmitLoading(true);
    const ladleOpen = data.PrevTap;
    const ladleEnd = data.End;
    const response = await upsertProcess(
      data.ProcessId === 0 ? null : data.ProcessId,
      ladleOpen,
      ladleEnd,
      true,
    );
    if (response.ok) {
      setNotificationMessage(
        "Process was " + (isNew ? "created" : "edited") + " successfully",
      );
      await getShiftDataFromAPI(ladleOpen, 0);
      const successResponse = response as {
        ok: true;
        data: { returnValue: number };
      };
      setProcessSelected(Number(successResponse.data.returnValue));
      setSelectedProcess(null);
      await refreshData();
    } else {
      setErrorMessage(response.message);
    }
    setIsSubmitLoading(false);
    onHide(true);
  };

  useEffect(() => {
    if (show) {
      setIsSubmitLoading(false);
      (async () => {
        setIsLoading(true);
        const process = selectedProcess;
        if (isNil(process)) {
          setIsLoading(false);
          return;
        }

        if (process.ProcessId === 0) {
          // New process
          setTitle("NEW EAF PROCESS");
          setIsNew(true);
          setIsNotEnd(true);
          reset({
            ProcessId: 0,
            PrevTap: shiftInfo?.CurrentStart || null,
            End: null,
          });
        } else {
          // Edit process
          setTitle("EDIT EAF PROCESS");
          setIsNew(false);

          const startDate =
            process.PrevTap === null || process.PrevTap === undefined
              ? shiftInfo?.CurrentStart || null
              : moment.utc(process.PrevTap).local().toDate();

          const endDate =
            process.End === null || process.End === undefined
              ? null
              : moment.utc(process.End).local().toDate();

          setIsNotEnd(process.End === null || process.End === undefined);

          reset({
            ProcessId: process.ProcessId,
            PrevTap: startDate,
            End: endDate,
          });
        }
        setIsLoading(false);
      })();
    } else {
      reset();
    }
  }, [show, selectedProcess, shiftInfo, reset]);

  return (
    <>
      <Grid container>
        <Grid item>
          <MesfModal
            maxWidth="lg"
            open={show}
            handleClose={() => {
              onHide(true);
            }}
            id="upsertEAFProcess"
            title={title}
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              <MesfModal.Content style={{ padding: "15px 30px" }}>
                {isLoading && (
                  <Grid
                    container
                    justifyContent="center"
                    alignItems="center"
                    style={{ minHeight: "300px" }}
                  >
                    <LazyLoading />
                  </Grid>
                )}
                {!isLoading && (
                  <>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6}>
                        <Controller
                          name="PrevTap"
                          control={control}
                          shouldUnregister={true}
                          rules={{
                            required: "Start date is required",
                            validate: {
                              notInFuture: (value) => {
                                if (value && value > new Date()) {
                                  return "Start date cannot be in the future.";
                                }
                                return true;
                              },
                            },
                          }}
                          render={({ field, fieldState: { error } }) => (
                            <FormControl error={!!error}>
                              <DateTimePicker
                                label="Start"
                                timezone={TimeService.getInstance().getServerTimeZone()}
                                format="MM/DD/YYYY HH:mm"
                                ampm={false}
                                value={moment(field.value)}
                                onChange={field.onChange}
                                inputRef={field.ref}
                                slotProps={{
                                  textField: {
                                    variant: "outlined",
                                    error: !!error,
                                    helperText: error?.message,
                                  },
                                }}
                              />
                            </FormControl>
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} md={6} style={{ margin: "auto" }}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              {...register("IsOpen")}
                              color="primary"
                              checked={isNotEnd}
                              onChange={(event) => {
                                setIsNotEnd(event.target.checked);
                              }}
                            />
                          }
                          label="Process not finished"
                        />
                      </Grid>
                    </Grid>
                    <Grid container spacing={1}>
                      <Grid item xs={12} md={6} style={{ marginTop: 10 }}>
                        {!isNotEnd && (
                          <Controller
                            name="End"
                            control={control}
                            shouldUnregister={true}
                            rules={{
                              required: !isNotEnd
                                ? "End date is required"
                                : false,
                              validate: {
                                notBeforeStart: (value, formValues) => {
                                  const startDate = formValues.PrevTap;
                                  if (
                                    value &&
                                    startDate &&
                                    value <= startDate
                                  ) {
                                    return "End date cannot be before Start date.";
                                  }
                                  return true;
                                },
                                notInFuture: (value) => {
                                  if (value && value > new Date()) {
                                    return "End date cannot be in the future.";
                                  }
                                  return true;
                                },
                              },
                            }}
                            render={({ field, fieldState: { error } }) => (
                              <FormControl error={!!error}>
                                <DateTimePicker
                                  label="End"
                                  format="MM/DD/YYYY HH:mm"
                                  ampm={false}
                                  timezone={TimeService.getInstance().getServerTimeZone()}
                                  value={moment(field.value)}
                                  onChange={field.onChange}
                                  disabled={isNotEnd}
                                  inputRef={field.ref}
                                  slotProps={{
                                    textField: {
                                      variant: "outlined",
                                      error: !!error,
                                      helperText: error?.message,
                                    },
                                  }}
                                />
                              </FormControl>
                            )}
                          />
                        )}
                      </Grid>
                    </Grid>
                  </>
                )}
              </MesfModal.Content>
              <MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
                <FooterModalButtons
                  isSubmitLoading={isSubmitLoading}
                  onHide={onHide}
                />
              </MesfModal.Actions>
            </form>
          </MesfModal>
        </Grid>
      </Grid>
    </>
  );
};
